import React, { useEffect, useState } from "react";
import axios from "axios";
import { Coins, Send, Sparkles, ArrowRight, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CoinsHeroBanner — vibrant hero banner shown on the landing page to
 * pitch the coin-earning + P2P-transfer value prop and attract signups.
 *
 * Props:
 *  - onCta?: () => void   Called when the primary CTA is clicked (opens the
 *                          register tab on Landing.jsx).
 *  - referralActive?: bool  Show a small "Convite ativo — +50 moedas" badge.
 */
export default function CoinsHeroBanner({ onCta, referralActive = false, content = {} }) {
  const c = content || {};
  const heroBadge   = c.hero_badge          || "Ganhe agora";
  const heroTitle   = c.hero_title          || "No Futuro PDV você ganha moedas por tudo que faz.";
  const copyPrefix  = c.hero_copy_prefix    || "Transfira para seus amigos na hora.";
  const copyCta     = c.hero_copy_cta_inline|| "Vem para o futuro!";
  const ctaLabel    = c.hero_cta            || "Criar conta grátis";
  const footnote    = c.hero_footnote       || "+moedas por post, curtida, corrida & convite";
  const refBadge    = c.hero_referral_badge || "🎁 Convite ativo · +50 moedas";
  const liveLabel   = c.live_counter_label  || "Ao vivo · últimas 24h";
  const liveUnit    = c.live_counter_unit   || "moedas distribuídas";
  const [live, setLive] = useState(null);
  const [missions, setMissions] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = () => axios.get(`${API}/hero/live-coins-today`)
      .then((r) => { if (mounted) setLive(r.data); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    axios.get(`${API}/hero/missions-summary`)
      .then((r) => { if (mounted) setMissions(r.data); })
      .catch(() => {});
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const coins24 = live?.total_coins_24h ?? 0;
  const users24 = live?.unique_users_24h ?? 0;

  return (
    <section
      data-testid="coins-hero-banner"
      className="relative overflow-hidden rounded-3xl mx-4 md:mx-6 mt-6 shadow-[0_20px_60px_-15px_rgba(234,179,8,0.35)]"
      style={{
        background:
          "linear-gradient(120deg, #f59e0b 0%, #f97316 25%, #dc2626 55%, #7c3aed 85%, #2563eb 100%)",
      }}
    >
      {/* subtle grain / noise overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-15 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35) 0, transparent 40%), radial-gradient(circle at 80% 60%, rgba(0,0,0,0.25) 0, transparent 45%)",
        }}
      />

      {/* floating coin glyphs */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <span className="absolute top-6 left-8 text-white/25 animate-[floatY_5s_ease-in-out_infinite]">
          <Coins className="w-10 h-10" />
        </span>
        <span className="absolute top-16 right-24 text-white/30 animate-[floatY_7s_ease-in-out_infinite]" style={{ animationDelay: "0.6s" }}>
          <Coins className="w-6 h-6" />
        </span>
        <span className="absolute bottom-8 left-24 text-white/20 animate-[floatY_6s_ease-in-out_infinite]" style={{ animationDelay: "1.2s" }}>
          <Coins className="w-14 h-14" />
        </span>
        <span className="absolute bottom-14 right-10 text-white/25 animate-[floatY_4.5s_ease-in-out_infinite]" style={{ animationDelay: "0.3s" }}>
          <Coins className="w-8 h-8" />
        </span>
      </div>

      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0px) rotate(-2deg); }
          50%      { transform: translateY(-14px) rotate(3deg); }
        }
        @keyframes shine {
          0%   { transform: translateX(-120%) skewX(-20deg); }
          60%  { transform: translateX(220%) skewX(-20deg); }
          100% { transform: translateX(220%) skewX(-20deg); }
        }
      `}</style>

      <div className="relative z-10 px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur border border-white/25 text-white text-[11px] font-bold uppercase tracking-widest" data-testid="coins-hero-badge">
              <Sparkles className="w-3.5 h-3.5" /> {heroBadge}
            </div>
            {referralActive && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-300/95 text-emerald-950 text-[11px] font-black uppercase tracking-widest animate-pulse shadow" data-testid="coins-hero-referral">
                {refBadge}
              </div>
            )}
          </div>
          <h2
            className="text-white text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-[1.1] drop-shadow-[0_3px_12px_rgba(0,0,0,0.35)]"
            data-testid="coins-hero-title"
          >
            {heroTitle.split("Futuro PDV").length === 2 ? (
              <>
                {heroTitle.split("Futuro PDV")[0]}
                <span className="inline-flex items-center gap-1"><Coins className="w-6 h-6 md:w-8 md:h-8 text-yellow-200" />Futuro PDV</span>
                {heroTitle.split("Futuro PDV")[1]}
              </>
            ) : heroTitle}
          </h2>
          <p
            className="mt-3 text-white/90 text-base md:text-lg max-w-2xl font-medium drop-shadow"
            data-testid="coins-hero-copy"
          >
            {copyPrefix}{" "}
            <span className="inline-flex items-center gap-1 font-bold text-white">
              {copyCta} <ArrowRight className="w-4 h-4" />
            </span>
          </p>

          {/* Live counter — pulsing */}
          <div className="mt-5 inline-flex items-center gap-3 rounded-2xl bg-black/30 backdrop-blur px-4 py-2.5 border border-white/15" data-testid="coins-hero-live">
            <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-yellow-300 text-yellow-950">
              <Coins className="w-4 h-4" />
              <span className="absolute inset-0 rounded-xl border-2 border-yellow-200/60 animate-ping" />
            </span>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-widest text-white/70 font-bold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                {liveLabel}
              </div>
              <div className="text-white font-black text-lg md:text-xl" data-testid="coins-hero-live-value">
                {coins24.toLocaleString("pt-BR")} <span className="text-white/80 text-sm font-bold">{liveUnit}</span>
              </div>
              {users24 > 0 && (
                <div className="text-white/70 text-[11px]" data-testid="coins-hero-live-users">
                  para <strong className="text-white">{users24.toLocaleString("pt-BR")}</strong> pessoas reais
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0 w-full md:w-auto">
          {missions && missions.count > 0 && (
            <div
              onClick={onCta}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCta && onCta(); }}
              data-testid="hero-missions-card"
              className="cursor-pointer group inline-flex items-center gap-3 rounded-2xl bg-white/95 hover:bg-white text-slate-900 px-4 py-3 shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white">
                <Target className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-yellow-400 text-yellow-950 text-[10px] font-black flex items-center justify-center shadow" data-testid="hero-missions-count">
                  {missions.count}
                </span>
              </span>
              <div className="leading-tight text-left">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">Missões de hoje</div>
                <div className="font-bold text-sm">
                  {missions.count} missão{missions.count === 1 ? "" : "s"} aberta{missions.count === 1 ? "" : "s"} · <span className="text-yellow-600" data-testid="hero-missions-total">+{missions.total_reward}</span> moedas
                </div>
              </div>
            </div>
          )}
          <button
            onClick={onCta}
            data-testid="coins-hero-cta"
            className="relative overflow-hidden group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-white text-slate-900 font-bold text-sm md:text-base shadow-lg hover:shadow-2xl hover:-translate-y-0.5 active:translate-y-0 transition-all"
          >
            <Send className="w-4 h-4" />
            {ctaLabel}
            <span
              aria-hidden
              className="absolute inset-y-0 -left-1/2 w-1/3 bg-white/70 blur-md pointer-events-none animate-[shine_3.2s_linear_infinite]"
            />
          </button>
          <div className="hidden md:flex items-center gap-2 text-white/85 text-[11px]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
            <span>{footnote}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
